import { APIError } from '../api'
import { parseAPIError } from './errors'
import type { Store } from 'vuex'

/**
 * Centralized error handler that sets errors in the Vuex store
 * and provides a consistent way to handle API errors across the app
 */
export function handleError(error: unknown, store: Store<unknown>): string {
  let errorMessage: string

  if (error instanceof APIError) {
    errorMessage = error.message
  } else if (error instanceof Error) {
    errorMessage = error.message
  } else {
    errorMessage = parseAPIError(error)
  }

  // Set error in store to display notification
  store.commit('SET_ERROR', errorMessage)

  return errorMessage
}

/**
 * Wrapper for async functions that automatically handles errors
 * Returns a tuple: [result, error]
 * If error occurred, result is null and error is the error message
 * If success, error is null and result is the return value
 */
export async function withErrorHandling<T>(
  fn: () => Promise<T>,
  store: Store<unknown>
): Promise<[T | null, string | null]> {
  try {
    const result = await fn()
    return [result, null]
  } catch (error) {
    const errorMessage = handleError(error, store)
    return [null, errorMessage]
  }
}
